import React, { useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronDown,
  ChevronRight,
  Download,
  FileJson,
  Copy,
  Trash2,
  RefreshCw,
  Search,
  Filter,
  X,
} from "lucide-react";
import { useAppLogs, useInvalidateAppLogs } from "@/hooks/useAppLogs";
import {
  clearAllEntries,
  downloadEntriesAsJson,
  downloadEntriesAsTxt,
  formatEntriesAsTxt,
  type LogCategory,
  type LogLevel,
  type LogEntryRow,
} from "@/services/logger/loggerStorage";
import { useToast } from "@/hooks/use-toast";

interface AppLogModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-applied filter (np. z konkretnego panelu — tylko 'audit' kategoria). */
  defaultCategory?: LogCategory[];
}

const ALL_CATEGORIES: LogCategory[] = [
  "content-engine",
  "map-repair",
  "audit",
  "existing-posts",
  "publishing",
  "auth",
  "api",
  "ui",
  "system",
];

const ALL_LEVELS: LogLevel[] = ["PUBLIC", "DEBUG", "ERROR", "WARN"];

const TIME_RANGE_OPTIONS = [
  { value: "all", label: "Wszystkie", hours: null },
  { value: "1h", label: "Ostatnia godzina", hours: 1 },
  { value: "24h", label: "Ostatnie 24h", hours: 24 },
  { value: "7d", label: "Ostatnie 7 dni", hours: 7 * 24 },
] as const;

export function AppLogModal({
  open,
  onOpenChange,
  defaultCategory,
}: AppLogModalProps) {
  const [selectedCategories, setSelectedCategories] = useState<LogCategory[]>(
    defaultCategory ?? [],
  );
  const [selectedLevels, setSelectedLevels] = useState<LogLevel[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [timeRange, setTimeRange] = useState<string>("24h");
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const { toast } = useToast();
  const invalidate = useInvalidateAppLogs();

  // Debounce search
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const since = useMemo(() => {
    const opt = TIME_RANGE_OPTIONS.find((o) => o.value === timeRange);
    if (!opt?.hours) return undefined;
    return new Date(Date.now() - opt.hours * 60 * 60 * 1000);
  }, [timeRange]);

  const { entries, total, isFetching, refetch } = useAppLogs({
    category: selectedCategories.length > 0 ? selectedCategories : undefined,
    level: selectedLevels.length > 0 ? selectedLevels : undefined,
    search: debouncedSearch || undefined,
    since,
    limit: 500,
    enabled: open,
  });

  const toggleCategory = (cat: LogCategory) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat],
    );
  };

  const toggleLevel = (lvl: LogLevel) => {
    setSelectedLevels((prev) =>
      prev.includes(lvl) ? prev.filter((l) => l !== lvl) : [...prev, lvl],
    );
  };

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedLevels([]);
    setSearch("");
    setTimeRange("24h");
  };

  const handleClearAll = useCallback(async () => {
    await clearAllEntries();
    invalidate();
    setShowClearConfirm(false);
    toast({ title: "Logi wyczyszczone", description: "IndexedDB wyczyszczona." });
  }, [invalidate, toast]);

  const handleDownloadTxt = () => {
    downloadEntriesAsTxt(entries);
    toast({ title: "Pobrano", description: `${entries.length} wpisów w TXT` });
  };

  const handleDownloadJson = () => {
    downloadEntriesAsJson(entries);
    toast({ title: "Pobrano", description: `${entries.length} wpisów w JSON` });
  };

  const handleCopy = async () => {
    const txt = formatEntriesAsTxt(entries);
    try {
      await navigator.clipboard.writeText(txt);
      toast({ title: "Skopiowano", description: `${entries.length} wpisów do schowka` });
    } catch {
      toast({
        title: "Nie udało się skopiować",
        description: "Brak dostępu do schowka",
        variant: "destructive",
      });
    }
  };

  const activeFilterCount =
    selectedCategories.length +
    selectedLevels.length +
    (debouncedSearch ? 1 : 0) +
    (timeRange !== "all" ? 1 : 0);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  Logi aplikacji
                  <Badge variant="outline" className="ml-2 font-mono">
                    {entries.length} / {total}
                  </Badge>
                </DialogTitle>
                <DialogDescription className="mt-1">
                  Wszystkie operacje (Content Engine, Map Repair, Audyt, Istniejące wpisy, Publikacja, Auth, API).
                  Persistowane w IndexedDB (max 1000 wpisów / 7 dni).
                </DialogDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => refetch()}
                disabled={isFetching}
                title="Odśwież"
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </DialogHeader>

          {/* Toolbar */}
          <div className="px-6 py-3 border-b space-y-3 bg-muted/30">
            <div className="flex flex-wrap items-center gap-2">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Szukaj w wiadomościach, prefixach, danych..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8"
                />
              </div>

              {/* Time range */}
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_RANGE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Category filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Filter className="h-4 w-4 mr-1" />
                    Kategoria {selectedCategories.length > 0 && `(${selectedCategories.length})`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-56">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground">Filtruj po kategorii</p>
                    {ALL_CATEGORIES.map((cat) => (
                      <label key={cat} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={selectedCategories.includes(cat)}
                          onCheckedChange={() => toggleCategory(cat)}
                        />
                        <span className="font-mono text-xs">{cat}</span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Level filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Filter className="h-4 w-4 mr-1" />
                    Poziom {selectedLevels.length > 0 && `(${selectedLevels.length})`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-48">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground">Filtruj po poziomie</p>
                    {ALL_LEVELS.map((lvl) => (
                      <label key={lvl} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={selectedLevels.includes(lvl)}
                          onCheckedChange={() => toggleLevel(lvl)}
                        />
                        <LevelBadge level={lvl} />
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-3 w-3 mr-1" />
                  Wyczyść filtry ({activeFilterCount})
                </Button>
              )}
            </div>
          </div>

          {/* Body — virtualized table */}
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              {entries.length === 0 ? (
                <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
                  {isFetching ? "Ładowanie..." : "Brak logów spełniających kryteria"}
                </div>
              ) : (
                <table className="w-full text-xs font-mono">
                  <thead className="sticky top-0 bg-background z-10 border-b">
                    <tr className="text-left text-muted-foreground">
                      <th className="px-4 py-2 w-8"></th>
                      <th className="px-2 py-2 w-32">Czas</th>
                      <th className="px-2 py-2 w-24">Poziom</th>
                      <th className="px-2 py-2 w-32">Kategoria</th>
                      <th className="px-2 py-2 w-32">Prefix</th>
                      <th className="px-2 py-2">Wiadomość</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry) => (
                      <LogRow
                        key={entry.id ?? `${entry.ts}-${entry.prefix}-${entry.msg}`}
                        entry={entry}
                        expanded={entry.id !== undefined && expandedIds.has(entry.id)}
                        onToggle={() => entry.id !== undefined && toggleExpand(entry.id)}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </ScrollArea>
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t flex items-center justify-between bg-muted/30">
            <div className="text-xs text-muted-foreground">
              Pokazano <span className="font-semibold">{entries.length}</span> z {total}
              {activeFilterCount > 0 && ` (${activeFilterCount} aktywnych filtrów)`}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleCopy} disabled={entries.length === 0}>
                <Copy className="h-3 w-3 mr-1" />
                Kopiuj
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownloadJson} disabled={entries.length === 0}>
                <FileJson className="h-3 w-3 mr-1" />
                JSON
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownloadTxt} disabled={entries.length === 0}>
                <Download className="h-3 w-3 mr-1" />
                TXT
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowClearConfirm(true)}
                disabled={total === 0}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Wyczyść wszystkie
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Wyczyścić wszystkie logi?</AlertDialogTitle>
            <AlertDialogDescription>
              Usunie {total} wpisów z IndexedDB. Operacja nieodwracalna.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearAll}>Wyczyść</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function LevelBadge({ level }: { level: LogLevel }) {
  const map: Record<LogLevel, { color: string; label: string }> = {
    PUBLIC: { color: "bg-blue-500/15 text-blue-600 dark:text-blue-400", label: "PUBLIC" },
    DEBUG: { color: "bg-gray-500/15 text-gray-600 dark:text-gray-400", label: "DEBUG" },
    ERROR: { color: "bg-red-500/15 text-red-600 dark:text-red-400", label: "ERROR" },
    WARN: { color: "bg-amber-500/15 text-amber-600 dark:text-amber-400", label: "WARN" },
  };
  const cfg = map[level];
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function CategoryBadge({ category }: { category: LogCategory }) {
  const colorMap: Record<LogCategory, string> = {
    "content-engine": "bg-purple-500/15 text-purple-600 dark:text-purple-400",
    "map-repair": "bg-orange-500/15 text-orange-600 dark:text-orange-400",
    audit: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400",
    "existing-posts": "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    publishing: "bg-pink-500/15 text-pink-600 dark:text-pink-400",
    auth: "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400",
    api: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400",
    ui: "bg-slate-500/15 text-slate-600 dark:text-slate-400",
    system: "bg-zinc-500/15 text-zinc-600 dark:text-zinc-400",
  };
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${colorMap[category]}`}>
      {category}
    </span>
  );
}

interface LogRowProps {
  entry: LogEntryRow;
  expanded: boolean;
  onToggle: () => void;
}

function LogRow({ entry, expanded, onToggle }: LogRowProps) {
  const time = entry.ts.split("T")[1]?.slice(0, 12) || entry.ts;
  const date = entry.ts.split("T")[0] || "";
  const hasData =
    entry.data && typeof entry.data === "object" && Object.keys(entry.data as object).length > 0;

  return (
    <>
      <tr
        className={`border-b border-border/40 hover:bg-muted/40 ${
          entry.level === "ERROR" ? "bg-red-500/5" : ""
        }`}
      >
        <td className="px-4 py-1.5">
          {hasData && (
            <button
              onClick={onToggle}
              className="text-muted-foreground hover:text-foreground"
              title={expanded ? "Zwiń" : "Rozwiń dane"}
            >
              {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </button>
          )}
        </td>
        <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">
          <div>{time}</div>
          <div className="text-[10px] opacity-60">{date}</div>
        </td>
        <td className="px-2 py-1.5">
          <LevelBadge level={entry.level} />
        </td>
        <td className="px-2 py-1.5">
          <CategoryBadge category={entry.category} />
        </td>
        <td className="px-2 py-1.5 text-foreground/80 truncate max-w-[150px]" title={entry.prefix}>
          {entry.prefix}
        </td>
        <td className="px-2 py-1.5 break-words">{entry.msg}</td>
      </tr>
      {expanded && hasData && (
        <tr>
          <td colSpan={6} className="px-12 py-2 bg-muted/40 border-b">
            <pre className="text-[10px] whitespace-pre-wrap break-words max-h-64 overflow-auto">
              {JSON.stringify(entry.data, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}
